import type { Route } from "./+types/home";
import { Link } from "react-router";
import ResumePrincipalPage from "../pages/ResumePrincipal";
import { RESUME_URL, isResumeHost } from "../lib/resumeHost";
import { content } from "../lib/content";
import { CTA, trackAttrs } from "../utils/track";
import { MAVEN_PROFILE_URL, OFFER_LABEL, OFFER_NOUN, formatSessionDate, formatDuration } from "../config/cta";
import { liveOffers } from "../lib/offers";
import { pageMeta } from "../lib/meta";
import { personSchema, websiteSchema } from "../components/content/SEO/SEO";
import { categoryAccent, categoryLabel } from "../utils/categoryAccent";
import { btnSolid, btnOutline } from "../utils/buttonAccent";
import { popularToShow } from "../utils/popular";
import HeroArtGrid from "../components/sections/HeroArtGrid/HeroArtGrid";
import InlineSubscribe from "../components/content/InlineSubscribe/InlineSubscribe";
import { HOME_PITCH } from "../config/subscribePitch";
import { HERO_ART } from "../config/heroArt";
import { OG_IMAGE } from "../config/heroArt";
import { ContentColumn } from "../components/layout/ContentColumn/ContentColumn";

// The flagship slot belongs to the next free live session, because it's the
// thing a reader can actually turn up to and the cheapest step into a cohort.
// Which session that is comes from the admin Maven table, so it changes itself:
// once a lesson has run it stops being live and the card comes down on its own.
// (build marker: force a clean rebuild — Coolify had reused a stale image)

export async function loader({ request }: Route.LoaderArgs) {
  // The resume host serves the CV at its root, and that is the only URL it
  // lives at. Rendering here (rather than redirecting to /resume-principal)
  // keeps the address bar on the bare domain, which is what gets shared.
  if (isResumeHost(new URL(request.url).hostname)) {
    return { isResume: true as const };
  }
  const [latest, clusters, offers, testimonials, popular] = await Promise.all([
    content.articles({ limit: 6 }),
    content.clusters(),
    liveOffers(),
    content.testimonials().catch(() => [] as any[]),
    // Real reader counts, so it's empty until people actually read. Never let a
    // counter take the homepage down.
    content.popular(7, 6).catch(() => [] as any[]),
  ]);

  // Social proof for the homepage. Keep the aggregate count honest (all
  // published testimonials) but only surface three quotes that read well at a
  // glance: real name + role, and a body short enough to scan without a "read
  // more". Selection happens in the loader so the component just renders.
  const testimonialCount = (testimonials as any[]).length;
  const eligibleTestimonials = (testimonials as any[])
    .filter((t) => t?.body && t?.name && t?.title)
    .map((t) => ({ id: t.id, name: String(t.name).trim(), title: String(t.title).trim(), body: String(t.body).trim() }))
    .filter((t) => t.body.length >= 90 && t.body.length <= 300);

  // Ten quotes clear the length filter and the section shows three, so taking
  // the first three meant seven people who wrote something good were never on
  // the page. Shuffle per request instead: a repeat visitor sees different
  // proof, and the whole pool earns its keep.
  //
  // Random is safe here because it happens in the loader. The result is
  // serialized and handed to the client, so the server and the browser render
  // the same three; picking in the component would hydrate a mismatch. It does
  // mean an edge cache in front of the HTML would freeze one shuffle, which is
  // a caching decision to make once rather than a reason to keep this static.
  const shuffled = [...eligibleTestimonials];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  const featuredTestimonials = shuffled.slice(0, 3);

  const popularThisWeek = popularToShow(popular as any[]);

  return { isResume: false as const, latest: latest.items, clusters, offers, testimonialCount, featuredTestimonials, popularThisWeek };
}

export function meta({ data }: Route.MetaArgs) {
  if (data?.isResume) {
    return [
      ...pageMeta({
        title: "Resume | Ehsan Gazar - Staff Software Engineer",
        description: "Resume of Ehsan Gazar, full-stack software engineer in London. Over a decade building scalable software across fintech, retail, wagering, enterprises, and startups.",
      }),
      // Canonical points at the resume host root, not gazar.dev, because that
      // is the only URL this page is served from.
      { tagName: "link", rel: "canonical", href: RESUME_URL } as const,
    ];
  }
  return pageMeta({
    title: "Gazar | From Demo to Production for LLM and Agent Systems",
    description: "Principal Engineer teaching engineers to take LLM and agent systems from a working demo to production: architecture, evaluations, observability, failure handling, cost and latency.",
    path: "/",
    image: OG_IMAGE.home,
    jsonLd: [websiteSchema(), personSchema()],
  });
}

// Pull a display URL out of the polymorphic image field ({url} | {url,formats} | string).
const imgUrl = (x: any): string | undefined =>
  x && typeof x === "object" ? x.formats?.medium?.url || x.url : typeof x === "string" ? x : undefined;

const IMG_CARD = "group block rounded-xl border border-brand-navy/12 bg-white overflow-hidden transition-shadow duration-150 hover:shadow-[0_2px_24px_-8px_rgba(28,78,128,0.3)]";
const THUMB = "w-full aspect-[16/9] object-cover bg-brand-navy/5";

// Branded fallback so no card is ever blank (some articles have no image). Shows
// a brand gradient + the category label instead of an empty/plain block.
function Thumb({ src, alt, label }: { src?: string; alt: string; label: string }) {
  return src ? (
    <img src={src} alt={alt} width={640} height={360} className={THUMB} loading="lazy" decoding="async" />
  ) : (
    <div className="flex w-full aspect-[16/9] items-center justify-center bg-gradient-to-br from-brand-navy to-brand-sky px-4" aria-hidden="true">
      <span className="text-center font-heading text-overline uppercase tracking-[0.16em] text-white/85">{label}</span>
    </div>
  );
}

// The three "More ways to learn" cards each carry a topic-family accent: a
// tinted surface, a left rule, and a filled accent button, so the row reads in
// colour rather than flat navy. Classes are literal so Tailwind's JIT picks
// them up.
const WAYS = [
  // `bar` and `link` are written out in full rather than composed from the
  // accent name: Tailwind only sees class names that appear literally in the
  // source, so a template string here would compile to nothing.
  // Prices and the count are read off `Course` in prod (12 Aug 2026: five published,
  // $99 to $299), not remembered. The previous copy said "Clean Code Mastery free" and it
  // has cost $99 since 23 July, so a reader who clicked "free" met a paywall; it also
  // named three of the five and omitted both $299 courses, which are the two most
  // expensive things on the site. Deliberately does not enumerate all five: a list this
  // card cannot hold is what drifted out of date in the first place.
  { to: "/courses", title: "Self-paced courses", body: "Five self-paced courses, $99 to $299, from clean code and system design to LLM and agent systems.", cta: "Browse courses", bar: "bg-brand-teal", link: "text-brand-teal" },
  { to: "/mentorship", title: "1:1 mentorship", body: "Career, system-design judgment, and the path from senior to staff and principal.", cta: "See programs", bar: "bg-brand-burgundy", link: "text-brand-burgundy" },
  { to: "/learnings", title: "Writing & podcast", body: "Hundreds of deep-dives on architecture, plus the Breakpoint podcast.", cta: "Read the latest", bar: "bg-brand-gold", link: "text-brand-gold" },
  // Persian surface. Links to the path, not to fa.gazar.dev: the subdomain only
  // 301s here, so routing internally saves the reader a redirect and keeps the
  // row a client-side <Link> like its neighbours. Kept in English because the
  // row sits on the English page; the page it opens is entirely Persian.
  { to: "/fa", title: "Persian courses", body: "The Farsi side of the site: eight video courses on JavaScript, React, Node and code quality, plus writing in Persian.", cta: "Open the Persian site", bar: "bg-brand-coral", link: "text-brand-coral" },
  // Coding used to be a row here. It now has its own band with the recording in
  // it, directly below this list, and the two were touching on screen and saying
  // the same sentence twice. The `?src=` tag moved to that band's link, so the
  // click is still attributable and still distinct from the TypeScript article
  // rail. The external-link branch in the renderer below is kept deliberately:
  // these are signposts and the next one added may well leave the site.
] as const;

export default function Home({ loaderData }: Route.ComponentProps) {
  if (loaderData.isResume) return <ResumePrincipalPage />;
  const { latest, clusters, offers, testimonialCount, featuredTestimonials, popularThisWeek } = loaderData;
  const flagship = offers.lesson;
  const goDeeper = offers.course;
  // The Maven block leads with the paid cohort and puts the free sessions
  // underneath it as boxes of their own (Gaz, 10 Oct 2026). The cohort is the
  // thing that pays; the free sessions are the way in, so they sit beside each
  // other at the same weight, with FREE as the loudest word on each.
  const cohort = goDeeper;
  const cohortOpen = Boolean(cohort.startsAt);
  const freeSessions = offers.all
    .filter((o) => o.kind === "lesson" && o.startsAt)
    .sort((a, b) => (a.startsAt! < b.startsAt! ? -1 : 1));
  // Everything else paid (cohorts on a waitlist) gets one line, not a card:
  // a waitlist is worth a link, not the space an open enrolment gets.
  const waitlists = offers.all.filter((o) => o.kind !== "lesson" && o.id !== cohort.id);

  // The longest quote leads. It isn't a quality judgement, it's a layout one:
  // the lead slot is set at reading size and a one-line quote looks stranded
  // there, while the same words sit fine in the narrower column beside it.
  const quotesByLength = [...featuredTestimonials].sort((a, b) => b.body.length - a.body.length);
  const leadQuote = quotesByLength[0];
  const supportingQuotes = quotesByLength.slice(1);

  // Clusters have no page of their own, so each card has to open on an article
  // inside it. Taking members[0] every time meant two clusters that share a
  // top article rendered as two cards with the same cover, the same link and
  // different titles: the oxlint piece leads both "Modern TypeScript tooling"
  // and "X vs Y: engineering trade-offs". Walk the members instead and take the
  // first one no earlier card has claimed.
  //
  // The real fix is a page per cluster, so a topic card leads to the topic
  // rather than to one piece of it. This keeps the section honest until then.
  const startHere = (() => {
    const claimed = new Set<string>();
    const picked: { cluster: any; pillar: any }[] = [];
    for (const c of clusters as any[]) {
      const members: any[] = c.members ?? [];
      const pillar =
        members.find((m) => m?.slug && !claimed.has(`${m.category}/${m.slug}`)) ?? members[0];
      if (pillar?.slug) claimed.add(`${pillar.category}/${pillar.slug}`);
      picked.push({ cluster: c, pillar });
      if (picked.length === 8) break;
    }
    return picked;
  })();

  return (
    <div>
      {/* ── Hero (full-bleed navy) ── */}
      <section className="bg-brand-navy text-white">
        <ContentColumn anchor="home-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="font-heading text-overline uppercase tracking-[0.2em] text-white/70 mb-5">
              Principal Engineer · 16+ years
            </p>
            {/* The production wedge, adopted 11 Aug 2026. The career framing it replaced
                ("think and design like architects") has a sample of zero: nobody has ever
                written in asking for it. The one buyer who did write in described the gap
                between a working LLM/agent demo and a system that survives production, and
                between a sample of one and a sample of none, one wins.

                Deliberately not blended with the career line. Saying both in one sentence
                is the mistake the 21 Jul Maven lesson made, and it produced the worst
                result of that run. The career-shaped surfaces stay as they are: the Senior
                to Staff cohort, the mentorship catalogue and the Maven public title. */}
            <h1 className="font-sans text-[40px] sm:text-[52px] lg:text-[58px] font-bold leading-[1.05] tracking-[-0.022em] text-white">
              Helping engineers take LLM and agent systems from demo to production.
            </h1>
            <p className="text-[18px] sm:text-[20px] text-white/70 mt-6 max-w-xl leading-relaxed">
              Architecture, evaluations, observability, failure handling, cost and latency: the parts a demo never has to answer for.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              {/* With a session scheduled, the free seat is the ask and the
                  cohort is the follow-up. With nothing scheduled, the cohort is
                  the ask — never advertise a session that doesn't exist. */}
              {flagship ? (
                <a href={flagship.url} target="_blank" rel="noopener noreferrer" {...trackAttrs(CTA.lesson, "home_hero", { target: flagship.id })} className={btnSolid("gold")}>
                  Join the free live session ↗
                </a>
              ) : (
                <a href={goDeeper.url} target="_blank" rel="noopener noreferrer" {...trackAttrs(CTA.maven, "home_hero", { target: goDeeper.id })} className={btnSolid("gold")}>
                  Join the live cohort ↗
                </a>
              )}
              <Link to="/mentorship" {...trackAttrs(CTA.mentorship, "home_hero")} className={btnOutline("sky")}>
                Work with me 1:1
              </Link>
            </div>
            {/* The secondary line names the kind, not the title: Maven titles
                run to ten words and wrap this onto three lines. */}
            {flagship ? (
              <a href={goDeeper.url} target="_blank" rel="noopener noreferrer" {...trackAttrs(CTA.maven, "home_hero", { target: goDeeper.id })} className="inline-flex items-center gap-1.5 mt-4 text-body-sm font-heading font-semibold text-brand-sky hover:text-white transition-colors">
                Or go deeper: {OFFER_NOUN[goDeeper.kind]} ↗
              </a>
            ) : (
              <a href={MAVEN_PROFILE_URL} target="_blank" rel="noopener noreferrer" {...trackAttrs(CTA.maven, "home_hero", { target: "maven-profile" })} className="inline-flex items-center gap-1.5 mt-4 text-body-sm font-heading font-semibold text-brand-sky hover:text-white transition-colors">
                See everything I teach on Maven ↗
              </a>
            )}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 font-heading text-caption text-white/65">
              <span>Live cohorts on Maven</span>
              <span className="text-white/30" aria-hidden="true">·</span>
              <span>500+ articles</span>
              <span className="text-white/30" aria-hidden="true">·</span>
              <span>The Breakpoint podcast</span>
            </div>
          </div>

          {/* The work, not the face. The portrait still leads /about, which is
              the page that's actually about me. */}
          <HeroArtGrid images={HERO_ART.home} />
        </ContentColumn>
      </section>

      <ContentColumn anchor="home-main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ── Maven: the cohort leads, the free sessions sit under it ── */}
        {/* A LIGHT card (not a second navy block) so it contrasts with the navy
            hero above. Flat, bordered, no shadows or gradients (house rules). */}
        <section className="py-10">
          <div className="rounded-2xl border border-brand-gold bg-white text-brand-navy p-6 sm:p-10">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
              <span className="inline-flex items-center gap-2 font-heading text-overline uppercase tracking-[0.16em] text-brand-red">
                {cohortOpen ? (
                  <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-red/70" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-red" />
                  </span>
                ) : null}
                {cohortOpen ? "Enrolling now" : "Waitlist open"}
              </span>
              <span className="font-heading text-caption text-brand-navy/45">{OFFER_LABEL[cohort.kind]}</span>
            </div>
            <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8 lg:gap-12 items-start">
              <div>
                <h2 className="font-heading text-h1 font-bold text-brand-navy leading-tight">{cohort.title}</h2>
                {cohort.blurb ? <p className="text-brand-navy/70 text-body-lg mt-4 max-w-2xl leading-relaxed">{cohort.blurb}</p> : null}
              </div>
              <div className="flex flex-col gap-4">
                {formatSessionDate(cohort.startsAt) ? (
                  <p className="m-0 rounded-smooth border border-brand-navy/15 px-4 py-3 text-body-sm font-heading font-semibold text-brand-navy leading-snug">
                    Starts {formatSessionDate(cohort.startsAt)} · live on Zoom
                  </p>
                ) : null}
                {cohort.promo ? (
                  <p className="m-0 flex items-start gap-3 rounded-smooth border border-brand-gold/50 bg-brand-gold/10 px-4 py-3 text-body-sm font-heading font-semibold text-brand-navy leading-snug">
                    <span className="text-brand-gold" aria-hidden="true">★</span>
                    {cohort.promo}
                  </p>
                ) : null}
                <a href={cohort.url} target="_blank" rel="noopener noreferrer" {...trackAttrs(CTA.maven, "home_flagship", { target: cohort.id })} className={btnSolid("gold", "lg")}>
                  {cohortOpen ? "Enroll on Maven ↗" : "Join the waitlist ↗"}
                </a>
              </div>
            </div>
          </div>

          {freeSessions.length ? (
            <div className="grid md:grid-cols-2 gap-5 mt-5">
              {freeSessions.map((m) => (
                <article key={m.id} className="flex flex-col rounded-2xl border border-brand-navy/15 bg-white p-6 sm:p-8">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <span className="rounded-smooth bg-brand-red px-3 py-1 font-heading text-h4 font-black uppercase tracking-wide text-white">
                      Free
                    </span>
                    <span className="font-heading text-caption font-semibold text-brand-navy/55 text-right">
                      {formatSessionDate(m.startsAt)}
                      {formatDuration(m.durationMins) ? ` · ${formatDuration(m.durationMins)}` : ""}
                    </span>
                  </div>
                  <span className="font-heading text-overline uppercase tracking-[0.14em] text-brand-navy/45 mb-2">{OFFER_LABEL[m.kind]}</span>
                  <h3 className="m-0 font-heading text-h3 font-bold text-brand-navy leading-snug">{m.title}</h3>
                  {m.blurb ? <p className="m-0 mt-3 text-brand-navy/65 text-body-sm leading-relaxed">{m.blurb}</p> : null}
                  <div className="mt-auto pt-6">
                    <a href={m.url} target="_blank" rel="noopener noreferrer" {...trackAttrs(CTA.lesson, "home_free_session", { target: m.id })} className={btnSolid("red")}>
                      Save my free seat ↗
                    </a>
                  </div>
                </article>
              ))}
            </div>
          ) : null}

          {waitlists.length ? (
            <p className="mt-5 text-body-sm font-heading text-brand-navy/60">
              Also on Maven, waitlist open:{" "}
              {waitlists.map((w, i) => (
                <span key={w.id}>
                  {i ? " · " : ""}
                  <a href={w.url} target="_blank" rel="noopener noreferrer" {...trackAttrs(CTA.maven, "home_waitlist", { target: w.id })} className="font-semibold text-brand-navy underline decoration-brand-navy/30 hover:decoration-brand-navy">
                    {w.title} ↗
                  </a>
                </span>
              ))}
            </p>
          ) : null}
        </section>

        {/* ── Social proof: what engineers say ── */}
        {/* Placed right under the paid flagship so real quotes back the ask.
            Three scannable quotes + an honest aggregate count, linking to the
            full wall of testimonials. */}
        {featuredTestimonials.length >= 3 ? (
          <section className="py-10">
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="block w-10 h-1 rounded-full bg-brand-gold mb-3" aria-hidden="true" />
                <h2 className="font-heading text-h3 font-bold text-brand-navy">What engineers say</h2>
                <div className="flex items-center gap-2.5 mt-2">
                  <span className="flex gap-0.5 text-brand-gold" aria-label="5 out of 5 stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20" aria-hidden="true">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </span>
                  <p className="m-0 text-brand-navy/65 text-body-sm">
                    {testimonialCount}+ engineers and colleagues, sharing what changed after we worked together.
                  </p>
                </div>
              </div>
              <Link to="/testimonials" className="hidden sm:inline text-sm font-semibold text-brand-navy/70 hover:text-brand-navy">
                Read all →
              </Link>
            </div>
            {/* Not three cards, and not three equal columns either. The page
                was running four 3-up grids in a row, so an even split here read
                as more of the same regardless of how light the styling got.

                One quote leads at reading size and the other two sit beside it,
                which gives the section a shape of its own and puts the strongest
                testimonial where the eye lands first. The card fills, borders,
                shadows, accent bars and per-quote star rows are gone: every
                testimonial is five stars, so those rows repeated three times and
                told a reader nothing. The stars now sit once, in the header. */}
            <div className="grid lg:grid-cols-[1.25fr_1fr] gap-x-12 gap-y-10 border-t border-brand-navy/12 pt-9">
              <figure className="m-0">
                <blockquote className="m-0 border-l-0 pl-0 text-brand-navy/85 text-body-lg leading-relaxed italic">
                  “{leadQuote.body}”
                </blockquote>
                <figcaption className="mt-6">
                  <div className="font-heading text-body-sm font-bold text-brand-navy leading-snug not-italic">{leadQuote.name}</div>
                  <div className="text-brand-navy/55 text-caption mt-0.5">{leadQuote.title}</div>
                </figcaption>
              </figure>

              <div className="flex flex-col gap-8 lg:border-l lg:border-brand-navy/12 lg:pl-12">
                {supportingQuotes.map((t) => (
                  <figure key={t.id} className="m-0">
                    <blockquote className="m-0 border-l-0 pl-0 text-brand-navy/75 text-body-sm leading-relaxed italic">
                      “{t.body}”
                    </blockquote>
                    <figcaption className="mt-3">
                      <span className="font-heading text-caption font-bold text-brand-navy not-italic">{t.name}</span>
                      <span className="text-brand-navy/50 text-caption not-italic">, {t.title}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
            <Link to="/testimonials" className="sm:hidden inline-block mt-5 text-sm font-semibold text-brand-navy/70 hover:text-brand-navy">
              Read all {testimonialCount}+ testimonials →
            </Link>
          </section>
        ) : null}

        {/* ── Secondary: self-paced courses, mentorship, writing ── */}
        {/* Three tinted panels with accent bars and solid buttons sat directly
            under three testimonial cards, which made six boxes in two rows and
            gave a secondary nav the same weight as the paid offer above it.
            These are signposts, so they read as type: a short accent rule for
            colour, and a text link instead of a button. The rule matches the
            one under every section heading, so the colour coding survives. */}
        <section className="py-10">
          <h2 className="font-heading text-h4 font-bold text-brand-navy mb-6">More ways to learn</h2>
          {/* A list, not a third 3-up grid. These are signposts to other parts
              of the site, so a row each reads faster than a column each and
              gives the page a change of rhythm between the quotes above and the
              article cards below. The accent dot keeps the colour coding. */}
          <ul className="m-0 list-none p-0 border-t border-brand-navy/12">
            {WAYS.map((w) => {
              // One row, two wrappers. The drills row leaves the site, and
              // react-router would treat an absolute URL as a client-side path
              // and route to a 404, so it needs a real anchor. Rendering the
              // contents once and choosing the wrapper keeps the two in step.
              const external = "external" in w && w.external;
              const rowClass =
                "group flex flex-col sm:flex-row sm:items-baseline gap-x-6 gap-y-1.5 py-5 hover:opacity-75 transition-opacity";
              const inner = (
                <>
                  <span className="flex items-baseline gap-3 sm:w-56 shrink-0">
                    <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${w.bar}`} aria-hidden="true" />
                    <span className="font-heading text-body font-bold text-brand-navy">{w.title}</span>
                  </span>
                  <span className="flex-1 text-brand-navy/65 text-body-sm leading-relaxed">{w.body}</span>
                  <span className={`shrink-0 font-heading text-body-sm font-semibold ${w.link} group-hover:underline`}>
                    {w.cta} →
                  </span>
                </>
              );
              return (
                <li key={w.to} className="border-b border-brand-navy/12">
                  {external ? (
                    <a href={w.to} target="_blank" rel="noopener noreferrer" className={rowClass}>
                      {inner}
                    </a>
                  ) : (
                    <Link to={w.to} className={rowClass}>
                      {inner}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        {/* ── Marlin and Coding: the free products, shown rather than described ── */}
        {/* The two things on this page a visitor can use without paying or
            booking anything. The video introduces both, one after the other,
            from real captures of marlin.gazar.dev and coding.gazar.dev; it is
            made in gazar-studio (composition GazarProductsIntro) and is silent.
            It is served from this origin rather than from either product's
            domain: cross-origin media has to clear media-src, and when it does
            not it fails silently, painting the poster and never starting, which
            is indistinguishable from a video that is simply broken. */}
        <section className="py-10">
          <div className="rounded-2xl border border-brand-navy/12 bg-brand-cream overflow-hidden">
            <div className="grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
              {/* preload="metadata" so the poster paints but the 3MB body is not
                  fetched for the majority who scroll straight past. */}
              <video
                className="w-full h-full object-cover bg-brand-cream aspect-[1280/840]"
                controls
                preload="metadata"
                playsInline
                poster="/intro/marlin-and-coding-poster.jpg"
              >
                <source src="/intro/marlin-and-coding.mp4" type="video/mp4" />
              </video>
              <div className="p-6 sm:p-8 flex flex-col justify-center">
                <span className="block font-heading text-overline uppercase tracking-[0.14em] text-brand-navy/55">
                  Two free tools I build
                </span>
                <h2 className="font-heading text-h3 font-bold text-brand-navy leading-snug mt-2">
                  Read what your agents did. Keep your hands in the code.
                </h2>
                <div className="mt-5">
                  <h3 className="font-heading text-body font-bold text-brand-navy">Marlin</h3>
                  <p className="text-brand-navy/65 text-body-sm mt-1 leading-relaxed">
                    An open-source terminal for running coding agents. See what each one changed,
                    read the diff, then stage and commit without leaving it. No account, no telemetry.
                  </p>
                  <a
                    href="https://marlin.gazar.dev?src=home-video"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 font-heading text-body-sm font-semibold text-brand-forest hover:underline"
                  >
                    See Marlin →
                  </a>
                </div>
                <div className="mt-5">
                  <h3 className="font-heading text-body font-bold text-brand-navy">Coding</h3>
                  <p className="text-brand-navy/65 text-body-sm mt-1 leading-relaxed">
                    One TypeScript problem, written from memory against hidden tests, on a schedule
                    that brings it back just before you would have forgotten it. No account needed to try one.
                  </p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-2">
                    <a
                      href="https://coding.gazar.dev?src=home-video"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-heading text-body-sm font-semibold text-brand-forest hover:underline"
                    >
                      Try a drill →
                    </a>
                    <Link
                      to="/leadership/recognition-is-not-recall"
                      className="font-heading text-body-sm font-semibold text-brand-navy/60 hover:text-brand-navy"
                    >
                      Why I built it
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Latest writing ── */}
        <section className="py-14">
          <div className="flex items-end justify-between mb-6">
            <div>
              <span className="block w-10 h-1 rounded-full bg-brand-teal mb-3" aria-hidden="true" />
              <h2 className="font-heading text-h3 font-bold text-brand-navy">Latest writing</h2>
            </div>
            <Link to="/learnings" className="text-sm font-semibold text-brand-navy/70 hover:text-brand-navy">View all →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {latest.map((a) => (
              <Link key={a.id} to={`/${a.category}/${a.slug}`} className={IMG_CARD}>
                <Thumb src={imgUrl(a.image)} alt={a.title.trim()} label={categoryLabel(a.category)} />
                <div className="p-5">
                  <span className={`inline-block font-heading text-overline uppercase tracking-[0.14em] px-2 py-0.5 rounded ${categoryAccent(a.category).tag}`}>{categoryLabel(a.category)}</span>
                  <h3 className="text-lg font-bold text-brand-navy mt-2 group-hover:opacity-80 transition-opacity leading-snug line-clamp-2">{a.title.trim()}</h3>
                  {a.description ? <p className="text-brand-navy/65 text-body-sm mt-2 line-clamp-2">{a.description}</p> : null}
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ── Popular this week ── */}
        {/* Actually measured: reader counts from the last 7 days, deduped to one
            per visitor per day and only counted from real browsers. Hidden
            entirely below 3 articles — see the loader. */}
        {popularThisWeek.length ? (
          <section className="pb-14">
            <span className="block w-10 h-1 rounded-full bg-brand-red mb-3" aria-hidden="true" />
            <h2 className="font-heading text-h3 font-bold text-brand-navy">Popular this week</h2>
            <p className="text-brand-navy/65 text-body-sm mt-2 mb-6">What other engineers have been reading in the last seven days.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {popularThisWeek.map((a: any) => (
                <Link key={a.id} to={`/${a.category}/${a.slug}`} className={IMG_CARD}>
                  <Thumb src={imgUrl(a.image)} alt={a.title.trim()} label={categoryLabel(a.category)} />
                  <div className="p-5">
                    <span className={`inline-block font-heading text-overline uppercase tracking-[0.14em] px-2 py-0.5 rounded ${categoryAccent(a.category).tag}`}>{categoryLabel(a.category)}</span>
                    <h3 className="text-lg font-bold text-brand-navy mt-2 group-hover:opacity-80 transition-opacity leading-snug line-clamp-2">{a.title.trim()}</h3>
                    {a.description ? <p className="text-brand-navy/65 text-body-sm mt-2 line-clamp-2">{a.description}</p> : null}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {/* ── Where to start ── */}
        {/* Was "Popular guides", which measured nothing: the query had no
            ORDER BY, so this was 6 of 8 clusters in whatever order Postgres
            returned. It's a curated set of topic hubs, so it says so now, and
            shows all of them in the order the admin sets. */}
        <section className="pb-16">
          <span className="block w-10 h-1 rounded-full bg-brand-coral mb-3" aria-hidden="true" />
          <h2 className="font-heading text-h3 font-bold text-brand-navy">Where to start</h2>
          <p className="text-brand-navy/65 text-body-sm mt-2 mb-6">The deep dives I'd point a senior engineer at first, grouped by topic.</p>
          {/* Two wide, with the cover beside the text rather than above it.
              Latest writing and Popular this week are both 3-up stacked cards,
              so a third one read as more of the same and this section stopped
              announcing itself as the considered entry point it is.

              Eight, so the 2-wide grid ends flush. There are 16 clusters, so
              the cut is a display choice, not the whole set — hence the link
              out. Keep the count even if you change it. */}
          <div className="grid sm:grid-cols-2 gap-5">
            {startHere.map(({ cluster: c, pillar }) => {
              const cover = pillar ? imgUrl(pillar.image) : undefined;
              const depth = c.members?.length ?? 0;
              return (
                <Link
                  key={c.id}
                  to={pillar ? `/${pillar.category}/${pillar.slug}` : "/learnings"}
                  className="group flex gap-4 sm:gap-5 rounded-xl border border-brand-navy/12 bg-white p-4 sm:p-5 hover:border-brand-navy/30 transition-colors"
                >
                  {/* The covers are 16:9, so the image keeps that shape and the
                      box is sized from its width. Cropping one into a full-height
                      strip beside the text threw away most of the frame, which is
                      how a card ended up showing half a word. `self-start` stops
                      the flex row stretching it back out of ratio. */}
                  <div className="w-32 sm:w-56 shrink-0 self-start overflow-hidden rounded-lg">
                    <Thumb src={cover} alt={c.title} label={pillar?.category ? categoryLabel(pillar.category) : "Guide"} />
                  </div>
                  {/* No category chip. The cluster title already names the topic,
                      so a TYPESCRIPT tag above "Modern TypeScript tooling" said it
                      twice, and the extra line pushed the text column taller than
                      an uncropped 16:9 cover can reach. */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-body font-bold text-brand-navy leading-snug group-hover:opacity-80 transition-opacity">{c.title}</h3>
                    <p className="text-brand-navy/65 text-body-sm mt-1.5 line-clamp-2">{c.description}</p>
                    <span className="inline-block mt-2.5 text-caption font-semibold text-brand-navy/55">
                      {depth ? `${depth} deep dives · ` : ""}Start reading →
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <Link to="/learnings" className="inline-block mt-6 text-sm font-heading font-semibold text-brand-navy/70 hover:text-brand-navy transition-colors">
            All {clusters.length} topics →
          </Link>
        </section>

        {/* ── Owned email capture ── */}
        {/* The homepage is the largest entry surface on the site (196 entry
            sessions in the 30 days to 22 Aug 2026) and carried no email capture
            at all, so the only page that converts, /newsletter, was reachable by
            roughly 8% of traffic.

            ONE placement, and it sits here at the foot on purpose. Everything
            above it is either an offer or the writing, so a reader who reaches
            this has already been shown what the list is made of; the ask lands
            after the value rather than in front of it. It is inline, not a popup
            and not a sticky bar, and it is below the flagship session card so it
            never competes with the paid ask for the same scroll.

            Tagged `home` and nothing else. A single tag is the whole point: the
            source column then reads this lever on its own, next to
            `newsletter-page` and `article:*`, rather than blurring into them.

            The copy is static by construction. This block is server-rendered, so
            a count, a date or anything locale-derived would hydrate a text
            mismatch, which is the open React #418 on WebKit. */}
        <InlineSubscribe source="home" pitch={HOME_PITCH} />
      </ContentColumn>
    </div>
  );
}
